<mat-card class="card-base-section members">
  <mat-card-header class="card-header">
    <mat-card-title class="card-header-title">Workspace Members</mat-card-title>
    <mat-card-subtitle class="description">
      Manage access and membership for the current workspace.
    </mat-card-subtitle>
  </mat-card-header>
  <div class="card-header-division"></div>

  <div class="members__content">
    <section aria-label="Add workspace member" [attr.aria-busy]="adding()">
      <form
        class="members__add-form"
        [formGroup]="form"
        (ngSubmit)="addMember()"
      >
        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <input
            matInput
            type="email"
            formControlName="email"
            required
            [readOnly]="adding() || !!roleRetry()"
          />
          @if (email.invalid) {
            <mat-error>{{ getErrorMessage() }}</mat-error>
          }
        </mat-form-field>
        <button
          mat-flat-button
          color="primary"
          type="submit"
          [disabled]="!canAdd() || form.invalid"
        >
          Add Member
        </button>
      </form>
      @if (rolesLoading()) {
        <p role="status">Loading member roles...</p>
      }
      @if (rolesError()) {
        <div class="members__error" role="alert">
          <span>{{ rolesError() }}</span>
          <button mat-stroked-button type="button" (click)="loadRoles()">
            Retry roles
          </button>
        </div>
      }
      @if (adding()) {
        <div class="members__progress" role="status">
          <mat-progress-bar
            mode="indeterminate"
            aria-label="Saving workspace access"
          ></mat-progress-bar>
          <span>Saving workspace access...</span>
        </div>
      }
      @if (addError()) {
        <div class="members__error" role="alert">
          <span>{{ addError() }}</span>
          @if (roleRetry()) {
            <button
              mat-stroked-button
              type="button"
              [disabled]="adding()"
              (click)="retryRoleAssignment()"
            >
              Retry role assignment
            </button>
          }
        </div>
      }
    </section>

    <section
      class="members__table-region"
      aria-label="Workspace members table"
      [attr.aria-busy]="membersLoading()"
    >
      <div
        class="members__toolbar"
        role="group"
        aria-label="Member search and membership filter"
      >
        <mat-form-field appearance="outline">
          <mat-label>Search by user or email</mat-label>
          <input matInput [formControl]="search" type="search" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Filter membership</mat-label>
          <mat-select [formControl]="ownerFilter">
            <mat-option value="all">All members</mat-option>
            <mat-option value="owner">Workspace owner</mat-option>
            <mat-option value="member">Non-owner members</mat-option>
          </mat-select>
        </mat-form-field>
        @if (hasFilters()) {
          <button mat-stroked-button type="button" (click)="clearFilters()">
            Clear filters
          </button>
        }
      </div>

      <div class="members__table-content">
        @if (membersLoading()) {
          <div
            class="members__loading"
            role="status"
            aria-label="Loading members"
          >
            <app-loading-overlay></app-loading-overlay>
          </div>
        } @else if (membersError()) {
          <div class="members__error" role="alert">
            <span>{{ membersError() }}</span>
            <button mat-stroked-button type="button" (click)="loadMembers()">
              Retry members
            </button>
          </div>
        } @else {
          <div class="members__table-scroll custom-scrollbar">
            <app-shared-table
              [columns]="columns"
              [data]="filteredRows()"
              [cellTemplates]="{ email: emailCell, isOwner: ownerCell }"
              [pagination]="true"
              [pageSize]="25"
              [emptyMessage]="emptyMessage()"
              ariaLabel="Workspace members"
            >
              <ng-template #rowActions let-row>
                <button
                  mat-icon-button
                  color="warn"
                  type="button"
                  [disabled]="row.isOwner"
                  [matTooltip]="
                    row.isOwner
                      ? 'The workspace owner cannot be removed'
                      : 'Remove member'
                  "
                  [attr.aria-label]="'Remove ' + row.email + ' from workspace'"
                  (click)="removeMember(row)"
                >
                  <ng-icon name="HeroTrash" aria-hidden="true"></ng-icon>
                </button>
              </ng-template>
            </app-shared-table>
          </div>
        }
      </div>
    </section>
  </div>
</mat-card>

<ng-template #emailCell let-row>
  <span class="members__email">{{ row.email }}</span>
</ng-template>
<ng-template #ownerCell let-row>
  @if (row.isOwner) {
    <app-shared-pill status="info">Workspace owner</app-shared-pill>
  } @else {
    <app-shared-pill status="neutral">Member</app-shared-pill>
  }
</ng-template>
